import { useState } from "react"
import { createFileRoute, Link, redirect } from "@tanstack/react-router"
import { BrainIcon, ImageIcon, MessageSquareIcon } from "lucide-react"
import { useLocale, useT } from "@/lib/i18n"
import { joinWaitlist } from "@/lib/publicApi"
import { Button, Input, Spinner, Toast } from "@/components/ui"
import { PlanCards } from "@/components/PlanCards"
import { PricingFaq } from "@/components/PricingFaq"
import { IntervalToggle, PublicPlanCta, TrialRibbon, usePricing } from "@/components/Pricing"
import { SiteFooter, SiteHeader } from "@/components/SiteChrome"

type Search = { invite?: string }

// Bare / → chat when signed in; the public landing otherwise.
export const Route = createFileRoute("/")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    invite: typeof s.invite === "string" && s.invite ? s.invite : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (context.auth.isLoggedIn()) {
      throw redirect({ to: "/chat" })
    }
  },
  component: LandingPage,
})

function LandingPage() {
  const t = useT()
  const { invite } = Route.useSearch()
  const p = usePricing()
  const waitlistMode = p.signupMode === "invite" && !invite

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-background text-foreground">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute left-[10%] top-[-4%] h-80 w-80 animate-blob-1 rounded-full bg-primary/20 blur-[110px]" />
        <div className="absolute right-[6%] top-[18%] h-72 w-72 animate-blob-2 rounded-full bg-violet/15 blur-[110px]" />
      </div>
      <div className="relative z-10">
        <SiteHeader />
        <main className="mx-auto w-full max-w-5xl px-4">
          {/* 1. hero */}
          <section className="grid items-center gap-8 pb-12 pt-6 md:grid-cols-[1.1fr_0.9fr] md:pt-14">
            <div className="flex flex-col items-start gap-5">
              <img src="/lucy-logo.png" alt="Lucy" className="h-20 w-20 rounded-full object-cover shadow-[0_8px_30px_rgba(192,140,255,0.5)] md:hidden" />
              <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight md:text-5xl">{t("landing.headline")}</h1>
              <p className="max-w-lg text-[15px] leading-relaxed text-muted-foreground md:text-base">{t("landing.subline")}</p>
              {waitlistMode ? (
                <WaitlistForm />
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <Link to="/signup" search={invite ? { invite } : {}} className="inline-flex h-10 items-center justify-center rounded-xl bg-primary px-5 text-sm font-medium text-primary-foreground shadow-[0_4px_14px_rgba(243,183,87,0.35)] hover:brightness-110">
                    {t("landing.ctaSignup")}
                  </Link>
                  <Link to="/login" className="inline-flex h-10 items-center justify-center rounded-xl border border-border bg-foreground/[0.04] px-5 text-sm font-medium hover:bg-foreground/[0.06]">
                    {t("landing.ctaSignin")}
                  </Link>
                </div>
              )}
              {waitlistMode && (
                <p className="text-xs text-muted-foreground">
                  {t("landing.haveInvite")} <Link to="/signup" className="text-foreground underline-offset-2 hover:underline">{t("landing.useInvite")}</Link>
                  <span aria-hidden> · </span>
                  <Link to="/login" className="text-foreground underline-offset-2 hover:underline">{t("landing.ctaSignin")}</Link>
                </p>
              )}
            </div>
            <div className="hidden justify-center md:flex">
              <img src="/lucy-logo.png" alt="Lucy" className="h-56 w-56 rounded-full object-cover shadow-[0_18px_60px_rgba(192,140,255,0.45)] ring-1 ring-border" />
            </div>
          </section>

          {/* 2. demo */}
          <section className="pb-14" aria-labelledby="demo-title">
            <h2 id="demo-title" className="font-display text-xl font-semibold tracking-tight">{t("landing.demoTitle")}</h2>
            <Demo />
          </section>

          {/* 3. features */}
          <section className="pb-14" aria-labelledby="features-title">
            <h2 id="features-title" className="font-display text-xl font-semibold tracking-tight">{t("landing.featuresTitle")}</h2>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <Feature icon={<BrainIcon className="h-5 w-5" />} title={t("landing.f1.title")} body={t("landing.f1.body")} />
              <Feature icon={<MessageSquareIcon className="h-5 w-5" />} title={t("landing.f2.title")} body={t("landing.f2.body")} />
              <Feature icon={<ImageIcon className="h-5 w-5" />} title={t("landing.f3.title")} body={t("landing.f3.body")} />
            </div>
          </section>

          {/* 4. pricing summary */}
          <section className="pb-14" aria-labelledby="pricing-title">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 id="pricing-title" className="font-display text-xl font-semibold tracking-tight">{t("landing.pricingTitle")}</h2>
                <p className="mt-1 text-[13px] text-muted-foreground">{t("landing.pricingSub")}</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <TrialRibbon days={p.trialDays} />
                <IntervalToggle value={p.interval} onChange={p.setInterval} />
              </div>
            </div>
            <div className="mt-5">
              {p.plans.isLoading && <div className="flex justify-center py-8"><Spinner /></div>}
              {p.plans.data && (
                <PlanCards
                  plans={p.plans.data.plans}
                  interval={p.interval}
                  proNote={p.trialDays > 0 ? t("pricing.trialRibbon", { days: p.trialDays }) : undefined}
                  renderCta={(plan) => <PublicPlanCta plan={plan} signupMode={p.signupMode} />}
                />
              )}
            </div>
            <p className="mt-3 text-[12px]">
              <Link to="/pricing" className="text-muted-foreground underline-offset-2 hover:text-foreground hover:underline">{t("pricing.title")} →</Link>
            </p>
          </section>

          {/* 5. FAQ */}
          <PricingFaq className="pb-4" />
        </main>
        {/* 6. footer */}
        <SiteFooter />
      </div>
    </div>
  )
}

/** Static chat exchange; bubbles fade in one after another, no network. */
function Demo() {
  const t = useT()
  const lines: Array<{ role: "user" | "assistant"; key: string }> = [
    { role: "user", key: "landing.demo.1" },
    { role: "assistant", key: "landing.demo.2" },
    { role: "user", key: "landing.demo.3" },
    { role: "assistant", key: "landing.demo.4" },
  ]
  return (
    <div className="mt-4 rounded-2xl border border-border bg-card/80 p-4 shadow-glass backdrop-blur md:p-6" data-testid="landing-demo">
      <div className="mx-auto flex max-w-2xl flex-col gap-3">
        {lines.map((m, i) => (
          <div
            key={m.key}
            className={`flex animate-[fade-in_500ms_ease-out_both] ${m.role === "user" ? "justify-end" : "justify-start"}`}
            style={{ animationDelay: `${300 + i * 900}ms` }}
          >
            {m.role === "assistant" && <img src="/lucy-logo.png" alt="" className="mr-2 mt-1 h-6 w-6 shrink-0 rounded-full object-cover" />}
            <div
              className={`min-w-0 max-w-[88%] break-words text-[14px] leading-[1.5] md:max-w-[75%] ${
                m.role === "user"
                  ? "rounded-[18px] rounded-br-[4px] bg-primary px-4 py-2.5 text-primary-foreground"
                  : "rounded-[18px] rounded-bl-[4px] bg-secondary px-4 py-2.5 text-foreground ring-1 ring-inset ring-border"
              }`}
            >
              {t(m.key)}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function Feature({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary">{icon}</div>
      <h3 className="mt-3 text-[15px] font-semibold">{title}</h3>
      <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{body}</p>
    </div>
  )
}

function WaitlistForm() {
  const t = useT()
  const locale = useLocale()
  const [email, setEmail] = useState("")
  const [note, setNote] = useState("")
  const [state, setState] = useState<"idle" | "busy" | "done" | "error">("idle")

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setState("busy")
    try {
      await joinWaitlist({ email: email.trim(), note: note.trim() || undefined, locale, source: "landing" })
      setState("done")
    } catch {
      setState("error")
    }
  }

  if (state === "done") {
    return <Toast>{t("landing.waitlist.done")}</Toast>
  }
  return (
    <form onSubmit={submit} className="w-full max-w-md space-y-2 rounded-2xl border border-border bg-card/90 p-4 shadow-glass backdrop-blur" aria-labelledby="waitlist-title">
      <div id="waitlist-title" className="text-[14px] font-semibold">{t("landing.waitlist.title")}</div>
      <p className="text-[12px] text-muted-foreground">{t("landing.waitlist.sub")}</p>
      <Input
        type="email"
        required
        autoComplete="email"
        aria-label={t("landing.waitlist.email")}
        placeholder={t("landing.waitlist.email")}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Input
        aria-label={t("landing.waitlist.note")}
        placeholder={t("landing.waitlist.note")}
        maxLength={500}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {state === "error" && <Toast variant="destructive">{t("landing.waitlist.error")}</Toast>}
      <Button type="submit" className="w-full" disabled={state === "busy"}>
        {state === "busy" ? <Spinner /> : null} {t("landing.ctaWaitlist")}
      </Button>
    </form>
  )
}
